import { type FormEvent, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { listProducts, receiveBatch } from '../../api/stock'
import { getMyPractices } from '../../api/staff'
import { Button, ButtonLink } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { FormField, inputClass, selectClass, textareaClass } from '../../components/ui/FormField'
import { NumberStepper } from '../../components/ui/NumberStepper'
import { useToast } from '../../components/ui/Toast'
import { useFetch } from '../../hooks/useFetch'

interface FormState {
  practice_id: string
  product_id: string
  batch_number: string
  expiry_date: string
  quantity: string
  cost_price: string
  notes: string
}

const EMPTY: FormState = {
  practice_id: '', product_id: '', batch_number: '',
  expiry_date: '', quantity: '', cost_price: '', notes: '',
}

interface FieldErrors {
  practice_id?: string
  product_id?: string
  quantity?: string
}

export default function StockReceiveForm() {
  const navigate = useNavigate()
  const toast = useToast()

  const [form, setForm] = useState<FormState>(EMPTY)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [apiError, setApiError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const { data: practices, loading: practicesLoading, error: practicesError } = useFetch(
    () => getMyPractices(),
    [],
  )

  useEffect(() => {
    if (practices && practices.length > 0 && !form.practice_id) {
      setForm((f) => ({ ...f, practice_id: String(practices[0].id) }))
    }
  }, [practices, form.practice_id])

  const selectedPracticeId = form.practice_id ? Number(form.practice_id) : null

  const { data: products, loading: productsLoading } = useFetch(
    () => {
      if (!selectedPracticeId) return Promise.resolve([])
      return listProducts({ practice_id: selectedPracticeId, is_active: true })
    },
    [selectedPracticeId],
  )

  const set = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setForm((f) => ({ ...f, [field]: e.target.value }))
    if (field === 'practice_id') setForm((f) => ({ ...f, practice_id: e.target.value, product_id: '' }))
  }

  const validate = (): boolean => {
    const errors: FieldErrors = {}
    if (!form.practice_id) errors.practice_id = 'Practice is required.'
    if (!form.product_id) errors.product_id = 'Product is required.'
    const qty = parseFloat(form.quantity)
    if (!form.quantity || isNaN(qty) || qty <= 0) errors.quantity = 'Enter a quantity greater than 0.'
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setApiError(null)
    if (!validate()) return
    setSubmitting(true)
    try {
      await receiveBatch({
        practice_id: Number(form.practice_id),
        product_id: Number(form.product_id),
        batch_number: form.batch_number.trim() || null,
        expiry_date: form.expiry_date || null,
        quantity: form.quantity.trim(),
        cost_price: form.cost_price.trim() || null,
        notes: form.notes.trim() || null,
      })
      toast.success('Stock received.')
      void navigate('/stock')
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Something went wrong.')
    } finally {
      setSubmitting(false)
    }
  }

  if (practicesLoading) return <div className="text-text-2 p-8">Loading…</div>
  if (practicesError) return <div className="p-8"><ErrorBox>{practicesError}</ErrorBox></div>
  if (!practices || practices.length === 0) {
    return <div className="p-8"><ErrorBox>No practices found.</ErrorBox></div>
  }

  return (
    <div>
      <h1 className="text-[1.4rem] font-bold text-text-heading mt-0 mb-7">
        Receive Stock
      </h1>

      <Card className="max-w-[680px]">
        {apiError && <ErrorBox className="mb-5">{apiError}</ErrorBox>}

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <SectionTitle>Batch Details</SectionTitle>

          <FormField label="Practice" htmlFor="r_practice" required error={fieldErrors.practice_id}>
            <select
              id="r_practice"
              className={selectClass(!!fieldErrors.practice_id)}
              value={form.practice_id}
              onChange={(e) => {
                setForm((f) => ({ ...f, practice_id: e.target.value, product_id: '' }))
              }}
              disabled={submitting}
            >
              <option value="">Select practice…</option>
              {practices.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </FormField>

          <FormField label="Product" htmlFor="r_product" required error={fieldErrors.product_id}>
            <select
              id="r_product"
              className={selectClass(!!fieldErrors.product_id)}
              value={form.product_id}
              onChange={set('product_id')}
              disabled={submitting || !selectedPracticeId || productsLoading}
            >
              <option value="">
                {productsLoading ? 'Loading products…' : 'Select product…'}
              </option>
              {products?.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.unit})</option>
              ))}
            </select>
            {!productsLoading && selectedPracticeId && products?.length === 0 && (
              <div className="mt-[0.4rem] text-[0.82rem] text-text-3">
                No products in your catalog yet.{' '}
                <Link to="/stock/products/new" className="text-accent font-semibold">
                  Add a product first
                </Link>
              </div>
            )}
          </FormField>

          <div className="grid grid-cols-2 gap-x-4 gap-y-0 max-sm:grid-cols-1">
            <FormField label="Batch number" htmlFor="r_batch">
              <input
                id="r_batch"
                className={inputClass()}
                type="text"
                value={form.batch_number}
                onChange={set('batch_number')}
                disabled={submitting}
                placeholder="e.g. LOT2024A"
              />
            </FormField>
            <FormField label="Expiry date" htmlFor="r_expiry">
              <input
                id="r_expiry"
                className={inputClass()}
                type="date"
                value={form.expiry_date}
                onChange={set('expiry_date')}
                disabled={submitting}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-0 max-sm:grid-cols-1">
            <FormField label="Quantity" htmlFor="r_qty" required error={fieldErrors.quantity}>
              <NumberStepper
                id="r_qty"
                hasError={!!fieldErrors.quantity}
                min={0.01}
                step={1}
                value={form.quantity}
                onChange={(v) => setForm((f) => ({ ...f, quantity: v }))}
                disabled={submitting}
                placeholder="0"
              />
            </FormField>
            <FormField label="Cost price (R)" htmlFor="r_cost">
              <input
                id="r_cost"
                className={inputClass()}
                type="number"
                min="0"
                step="0.01"
                value={form.cost_price}
                onChange={set('cost_price')}
                disabled={submitting}
                placeholder="0.00"
              />
            </FormField>
          </div>

          <FormField label="Notes" htmlFor="r_notes">
            <textarea
              id="r_notes"
              className={textareaClass()}
              value={form.notes}
              onChange={set('notes')}
              disabled={submitting}
              placeholder="Supplier, invoice reference…"
            />
          </FormField>

          <div className="flex gap-3 pt-6 border-t border-border">
            <Button type="submit" size="lg" disabled={submitting}>
              {submitting ? 'Saving…' : 'Receive Stock'}
            </Button>
            <ButtonLink to="/stock" variant="outline" size="lg">Cancel</ButtonLink>
          </div>
        </form>
      </Card>
    </div>
  )
}
